// The till's carte — what the bar and the restaurant sell (docs/PMS-TILL-BRIEF.md §15, §16).
//
// A FUNCTIONAL CORE, as everywhere in this package: types and pure functions. The database is the enforcer —
// `pms_product`'s constraints and triggers (migration 20261019210100) refuse a bad row whatever writes it. The rules
// below mirror them so the page can say what is wrong before the round trip, in French, next to the field.

import type { Cents, ProductId } from './types.js';

/** A till product's part (GLOSSARY « Article / Formule / Option / Prix libre »). */
export type TillRole = 'item' | 'formule' | 'option' | 'open_price';

export const TILL_ROLE_LABELS_FR: Readonly<Record<TillRole, string>> = {
  item: 'Article',
  formule: 'Formule',
  option: 'Option',
  open_price: 'Prix libre',
};

/** The button colours the till screen draws — Lightspeed's, kept (T2-Q6). */
export const TILL_COLOURS = [
  'blue', 'dark_red', 'orange', 'pink', 'brown', 'yellow', 'white', 'cyan', 'green', 'light_red',
] as const;
export type TillColour = (typeof TILL_COLOURS)[number];

export const TILL_COLOUR_LABELS_FR: Readonly<Record<TillColour, string>> = {
  blue: 'Bleu', dark_red: 'Bordeaux', orange: 'Orange', pink: 'Rose', brown: 'Brun', yellow: 'Jaune',
  white: 'Blanc', cyan: 'Cyan', green: 'Vert', light_red: 'Rouge clair',
};

/** A CA category (`is_category`): where a till product's TVA comes from. */
export interface TillCategory {
  id: ProductId;
  code: string;
  labelFr: string;
  tvaRate: number;
}

/** One product of the carte, with what the formule structure says about it — shown, not edited (C-Q1). */
export interface TillProduct {
  id: ProductId;
  code: string;
  labelFr: string;
  role: TillRole;
  categoryId: ProductId;
  tvaRate: number;
  priceCents: Cents;
  screen: string | null;
  colour: TillColour | null;
  lsSku: string | null;
  vegaCode: string | null;
  active: boolean;
  sortOrder: number;
  /** The questions it asks when rung, in order (« Formule → Entrée », « Choix sauce »). */
  asks: string[];
  /** What a formule contains without a question; `required: false` is an optional supplement. */
  contains: { labelFr: string; required: boolean }[];
  /** The questions it is a choice of. */
  choiceOf: string[];
}

export interface TillCarte {
  categories: TillCategory[];
  products: TillProduct[];
}

/**
 * A product as the page TYPES it. `id` absent creates. The role is fixed once created — the adapter never writes
 * it on an update — because changing it would break the formule structure the product sits in, which this version
 * does not edit (C-Q1); for the same reason a formule is never created here. The code is the adapter's to mint
 * for a new product, never typed — nobody at the desk should have to invent a key.
 */
export interface TillProductDraft {
  id?: ProductId;
  role: TillRole;
  labelFr: string;
  categoryId: ProductId;
  priceCents: Cents;
  screen: string | null;
  colour: TillColour | null;
  sortOrder: number;
}

/** The euros a person types (« 4,50 », « 4.5 », « 12 ») → cents, or null when it is not an amount. */
export function tillParseEuros(text: string): Cents | null {
  const t = text.trim().replace(/\s/g, '').replace(',', '.');
  if (!/^\d{1,5}(\.\d{1,2})?$/.test(t)) return null;
  return Math.round(Number(t) * 100);
}

export function tillFormatEuros(cents: Cents): string {
  return (cents / 100).toFixed(2).replace('.', ',');
}

/**
 * What is wrong with a draft, in French, or null. The same rules as the database (T2): an item is priced, an open
 * price is not, an option may be 0 € (a choice that adds nothing); a label is never blank; the category exists; a
 * product inside a formule keeps its rate (« une ligne, un taux »).
 */
export function tillDraftProblem(
  draft: TillProductDraft,
  carte: TillCarte,
): string | null {
  if (draft.labelFr.trim() === '') return 'Le libellé est obligatoire.';
  if (draft.labelFr.trim().length > 120) return 'Le libellé dépasse 120 caractères.';
  const category = carte.categories.find((c) => c.id === draft.categoryId);
  if (!category) return 'Choisissez une catégorie.';
  if (!Number.isInteger(draft.priceCents) || draft.priceCents < 0) return 'Le prix est un montant en euros, par exemple 4,50.';
  if (draft.id === undefined && draft.role === 'formule') return 'Une formule se crée avec sa composition : pas depuis cet écran.';
  if (draft.role === 'item' && draft.priceCents === 0) return 'Un article se vend à un prix : un article à 0 € ne peut pas être encaissé.';
  if (draft.role === 'open_price' && draft.priceCents !== 0) return 'Un produit à prix libre n\'a pas de prix : le montant est saisi à la caisse.';
  if (draft.colour !== null && !(TILL_COLOURS as readonly string[]).includes(draft.colour)) return 'Couleur inconnue.';
  if (draft.id !== undefined) {
    const current = carte.products.find((p) => p.id === draft.id);
    if (!current) return 'Ce produit n\'est plus dans la carte.';
    const inStructure = current.asks.length > 0 || current.contains.length > 0 || current.choiceOf.length > 0;
    if (inStructure && category.tvaRate !== current.tvaRate) {
      return `« ${current.labelFr} » fait partie d'une formule à ${current.tvaRate} % : sa catégorie doit rester à ce taux.`;
    }
  }
  return null;
}

/** The categories a product may take: all of them, or only those at its rate when it sits in a formule. */
export function tillCategoriesFor(product: TillProduct | null, carte: TillCarte): TillCategory[] {
  if (product === null) return carte.categories;
  const inStructure = product.asks.length > 0 || product.contains.length > 0 || product.choiceOf.length > 0;
  return inStructure ? carte.categories.filter((c) => c.tvaRate === product.tvaRate) : carte.categories;
}

/** The carte by screen, in the order the till shows it: screens by their first product, products by their order.
 *  Options sit apart — they are never a button of their own. */
export function tillScreens(products: readonly TillProduct[]): { screen: string; products: TillProduct[] }[] {
  const bySort = [...products].sort((a, b) => a.sortOrder - b.sortOrder || a.labelFr.localeCompare(b.labelFr, 'fr'));
  const groups = new Map<string, TillProduct[]>();
  for (const p of bySort) {
    const key = p.role === 'option' ? OPTIONS_SCREEN : (p.screen ?? NO_SCREEN);
    const list = groups.get(key) ?? [];
    list.push(p);
    groups.set(key, list);
  }
  const rank = (s: string) => (s === OPTIONS_SCREEN ? 2 : s === NO_SCREEN ? 1 : 0);
  return [...groups.entries()]
    .sort((a, b) => rank(a[0]) - rank(b[0]))
    .map(([screen, list]) => ({ screen, products: list }));
}

export const OPTIONS_SCREEN = 'Options (choisies dans une formule)';
export const NO_SCREEN = 'Sans écran';
